import axios, { isAxiosError } from 'axios';
import { mainApi } from '../api';
import { logWithSentry, SENTRY_FEATURES } from '@/lib';

// TODO: Implement ZOD or something similar for request/response?
// TODO: Double check Sentry logging - do we need something more?

type UploadOptions = {
    objectType?: string;
    onProgress?: (percent: number) => void;
    onRetry?: (tryCount: number, error: Error) => void;
    maxRetries?: number;
    signal?: AbortSignal;
};

type S3Fields = Record<string, string>;

// Extended Error types for custom properties
interface FileSizeError extends Error {
    isFileSizeError: boolean;
}

interface UploadError extends Error {
    code?: string;
    logContext?: Record<string, unknown>;
}

const getFileInfo = (file: File) => ({
    fileName: file.name,
    fileType: file.type,
    fileSize: file.size,
});

const toError = (err: unknown): Error => (err instanceof Error ? err : new Error(String(err)));

// Helper functions
const stringifyForSentry = (obj: Record<string, unknown>): Record<string, string> => {
    const result: Record<string, string> = {};
    for (const [key, value] of Object.entries(obj)) {
        if (value === null || typeof value === 'string' || typeof value === 'number' || typeof value === 'boolean') {
            result[key] = String(value);
        } else if (value instanceof File) {
            result[key] = `[File: ${value.name}, size: ${value.size}]`;
        } else if (value instanceof FormData) {
            result[key] = '[FormData]';
        } else if (typeof value === 'object' && value !== null) {
            result[key] = JSON.stringify(value);
        } else {
            result[key] = String(value);
        }
    }
    return result;
};
// Helper to format bytes as human readable string
const humanFileSize = (bytes: number): string => {
    if (bytes < 1024) return bytes + ' B';
    const units = ['KB', 'MB', 'GB', 'TB', 'PB'];
    let u = -1;
    do {
        bytes /= 1024;
        ++u;
    } while (bytes >= 1024 && u < units.length - 1);
    return bytes.toFixed(1) + ' ' + units[u];
};

const logUploadError = (event: string, file: File, context: Record<string, unknown>) => {
    logWithSentry(event, {
        tags: { feature: SENTRY_FEATURES.FILE_UPLOAD },
        context: { ...getFileInfo(file), ...stringifyForSentry(context) },
    });
};

const getPresignedUrl = async (file: File, objectType?: string) => {
    const payload = {
        file_type: file.type,
        file_name: file.name.toLowerCase(),
        file_size: file.size,
        object_type: objectType || '',
    };

    let response;
    try {
        response = await mainApi.post('v1/generate-presigned-url', payload);
    } catch (err: unknown) {
        if (isAxiosError(err) && err.response?.data?.error === 'incorrect file size') {
            const maxSize = err.response.data.limits?.max;
            const maxSizeFormatted = maxSize ? humanFileSize(maxSize) : 'the allowed limit';
            const error = new Error(`File is too large. Maximum allowed size is ${maxSizeFormatted}.`) as FileSizeError;
            error.isFileSizeError = true;
            throw error;
        }
        throw err;
    }

    const s3Data = response.data?.data; // Backend returns data nested under 'data'
    if (!s3Data?.url || !s3Data?.fields) {
        const error = new Error('Failed to get presigned URL from server') as UploadError;
        error.logContext = { payload };
        throw error;
    }
    return { s3Data, response };
};

const buildFormData = (fields: S3Fields, file: File) => {
    const formData = new FormData();
    Object.entries(fields).forEach(([key, value]) => {
        formData.append(key, value);
    });
    formData.append('file', file);
    return formData;
};

const parseS3Error = (responseText: string | null) => {
    if (!responseText) return {};
    try {
        const parser = new DOMParser();
        const resp = parser.parseFromString(responseText, 'application/xml');
        const code = resp.querySelector('Code')?.textContent;
        const maxSizeAllowed = resp.querySelector('MaxSizeAllowed')?.textContent;
        return { code, maxSizeAllowed };
    } catch (err) {
        logWithSentry('File upload - S3 XML parse error', {
            tags: { feature: SENTRY_FEATURES.FILE_UPLOAD },
            context: { error: toError(err).message, responseText: responseText.slice(0, 500) },
        });
        return {};
    }
};

const uploadToS3 = (
    url: string,
    formData: FormData,
    key: string,
    response: { data?: { url?: string } },
    options?: UploadOptions,
    tryCount?: number
): Promise<{ objectKey: string; url: string }> => {
    return new Promise((resolve, reject) => {
        axios
            .post(url, formData, {
                onUploadProgress: event => {
                    if (event.lengthComputable && options?.onProgress && event.total && event.total > 0) {
                        const percent = Math.round((event.loaded / event.total) * 100);
                        options.onProgress(percent);
                    }
                },
                signal: options?.signal,
            })
            .then(_ => {
                const result = { objectKey: key, url: response.data?.url as string };
                resolve(result);
            })
            .catch(error => {
                // Parse S3 error code if available
                const { code, maxSizeAllowed } = parseS3Error(error.response?.data);
                let errorMsg = `${error.message}`;
                const logContext: Record<string, unknown> = {
                    message: error.message,
                    code: error.code,
                    name: error.name,
                    stack: error.stack,
                    status: error.response?.status,
                    statusText: error.response?.statusText,
                    responseData:
                        typeof error.response?.data === 'string'
                            ? error.response.data.slice(0, 500)
                            : JSON.stringify(error.response?.data)?.slice(0, 500),
                    responseHeaders: error.response?.headers,
                    url: error.config?.url,
                    method: error.config?.method,
                    tryCount,
                };

                if (code === 'EntityTooLarge') {
                    errorMsg = `Upload file error: entity too large, max size allowed ${maxSizeAllowed}`;
                    logContext.maxSizeAllowed = maxSizeAllowed;
                } else if (code) {
                    logContext.code = code;
                }
                const uploadError = new Error(errorMsg) as UploadError;
                uploadError.logContext = logContext;
                reject(uploadError);
            });
    });
};

export async function getPresignedUrlOnly(
    file: File,
    objectType?: string
): Promise<{
    objectKey: string;
    url: string;
    fields: S3Fields;
    s3Data: { url: string; fields: S3Fields };
    response: { data: { url: string } };
}> {
    const { s3Data, response } = await getPresignedUrl(file, objectType);
    return {
        objectKey: s3Data.fields.key,
        url: s3Data.url,
        fields: s3Data.fields,
        s3Data,
        response,
    };
}

export async function uploadFile(
    file: File,
    options: UploadOptions & {
        presignedData?: { url: string; fields: S3Fields; key: string; response: { data?: { url?: string } } };
    } = {}
): Promise<{ objectKey: string; url: string }> {
    const maxRetries = options?.maxRetries ?? 0;
    let tryCount = 0;
    let lastError: Error | null = null;

    while (tryCount <= maxRetries) {
        try {
            let s3Data, response, url, fields, key;
            if (options?.presignedData) {
                ({ url, fields, key, response } = options.presignedData);
                s3Data = { url, fields, key };
            } else {
                ({ s3Data, response } = await getPresignedUrl(file, options?.objectType));
                url = s3Data.url;
                fields = s3Data.fields;
                key = s3Data.fields.key;
            }
            const formData = buildFormData(fields, file);
            const result = await uploadToS3(url, formData, key, response, options, tryCount);
            return result;
        } catch (err) {
            lastError = toError(err);
            let shouldRetry = false;

            // Only retry if request timeout or S3 error code is present
            if ((lastError as UploadError).code === 'ECONNABORTED') {
                shouldRetry = true;
            }

            // Check for S3 error code (if available in logContext)
            const logContext = (lastError as UploadError).logContext || {};
            const code = logContext.code;
            if (code && code !== 'EntityTooLarge' && tryCount < maxRetries) {
                shouldRetry = true;
            }
            tryCount++;
            if (!shouldRetry || tryCount > maxRetries) {
                logUploadError('File upload - Error', file, logContext);
                throw lastError;
            } else {
                options.onRetry?.(tryCount, lastError);
            }
        }
    }
    throw lastError ?? new Error('File upload failed');
}
